
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mail, ShieldCheck } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Admin } from "@/types/admin.types";

interface AdminResultCardProps {
  admin: Admin;
  query: string;
  highlightText: (text: string, highlight: string) => React.ReactNode;
}

const AdminResultCard = ({ admin, query, highlightText }: AdminResultCardProps) => {
  const adminName = `${admin.firstName || ''} ${admin.lastName || ''}`.trim();
  const initials = (admin.firstName?.[0] || '') + (admin.lastName?.[0] || '');
  
  return (
    <Link to={`/admins/${admin.id}`}>
      <Card className="mb-4 hover:shadow-md transition-shadow duration-200 overflow-hidden relative">
        <CardContent className="p-4 flex gap-4 items-start">
          <Avatar className="h-12 w-12 border">
            <AvatarImage src={admin.profileImage} alt={adminName} />
            <AvatarFallback className="bg-purple-100 dark:bg-purple-800 text-purple-600 dark:text-purple-200">
              {initials}
            </AvatarFallback>
          </Avatar>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-lg truncate">
              {highlightText(adminName, query)}
            </h3>
            
            <div className="space-y-1 mt-2">
              {admin.email && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <Mail className="h-3.5 w-3.5 mr-2 flex-shrink-0" />
                  <span className="truncate">{highlightText(admin.email, query)}</span>
                </div>
              )}
              
              {admin.role && (
                <div className="flex items-center text-sm text-muted-foreground">
                  <ShieldCheck className="h-3.5 w-3.5 mr-2 flex-shrink-0" />
                  <span className="truncate">{highlightText(admin.role, query)}</span>
                </div>
              )}
            </div>
          </div>
          
          <Badge variant="outline" className="absolute top-2 right-2 bg-purple-50 dark:bg-purple-900 text-purple-700 dark:text-purple-300">
            Admin
          </Badge>
        </CardContent>
      </Card>
    </Link>
  );
};

export default AdminResultCard;
